/**
 * CONSAI Business App - Admin Analytics Page
 * Displays admin analytics with metrics, charts, and user activity
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  Users, Crown, Clock, Timer, ArrowLeft, Search, 
  ChevronUp, ChevronDown, Loader2, RefreshCw, CalendarIcon,
  BarChart3, TrendingUp, Activity, Download, CheckCircle, XCircle,
  ShoppingCart, Package, CreditCard, Receipt
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAnalytics = () => {
  const { t, language } = useLanguage();
  const { admin, checkAdminAuth } = useAuth();
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [onboardingMetrics, setOnboardingMetrics] = useState(null);
  const [userActivity, setUserActivity] = useState([]);
  const [dateFilter, setDateFilter] = useState('30days');
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(1);
  const perPage = 20;

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('date_filter', dateFilter);
      if (dateFilter === 'custom' && customDateRange.from) {
        params.append('from_date', format(customDateRange.from, 'yyyy-MM-dd'));
        if (customDateRange.to) {
          params.append('to_date', format(customDateRange.to, 'yyyy-MM-dd'));
        }
      }
      if (searchQuery) params.append('search', searchQuery);
      params.append('sort_field', sortField);
      params.append('sort_direction', sortDirection);
      params.append('page', page);
      params.append('per_page', perPage);

      const response = await axios.get(`${API_URL}/api/admin/analytics?${params}`, {
        withCredentials: true
      });
      
      setMetrics(response.data.metrics);
      setOnboardingMetrics(response.data.onboarding_metrics);
      setUserActivity(response.data.user_activity);
      setTotalUsers(response.data.total_users);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      } else {
        toast.error('Failed to load analytics');
      }
    } finally {
      setLoading(false);
    }
  }, [dateFilter, customDateRange, searchQuery, sortField, sortDirection, page, navigate]);

  // Export to Excel
  const handleExport = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      params.append('date_filter', dateFilter);
      if (dateFilter === 'custom' && customDateRange.from) {
        params.append('from_date', format(customDateRange.from, 'yyyy-MM-dd'));
        if (customDateRange.to) {
          params.append('to_date', format(customDateRange.to, 'yyyy-MM-dd'));
        }
      }

      const response = await axios.get(`${API_URL}/api/admin/analytics/export?${params}`, {
        withCredentials: true,
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from content-disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'consai_analytics.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'hi' ? 'डाउनलोड सफल' : 'Export downloaded successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(language === 'hi' ? 'एक्सपोर्ट विफल' : 'Failed to export');
    } finally {
      setExporting(false);
    }
  };

  // Initial load
  useEffect(() => {
    const initAdmin = async () => {
      const adminData = await checkAdminAuth();
      if (!adminData) {
        navigate('/admin');
        return;
      }
      await fetchAnalytics();
    };
    initAdmin();
  }, [checkAdminAuth, navigate, fetchAnalytics]);

  // Refetch when filters change
  useEffect(() => {
    if (!loading) {
      fetchAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, customDateRange, sortField, sortDirection, page]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!loading) {
        setPage(1);
        fetchAnalytics();
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setPage(1);
  };

  // Render sort icon
  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp className="h-4 w-4 inline ml-1" /> : 
      <ChevronDown className="h-4 w-4 inline ml-1" />;
  };

  // Calculate pie chart segments
  const renderPieChart = () => {
    if (!metrics) return null;
    
    const total = (metrics.premium_users || 0) + (metrics.starter_users || 0) + (metrics.trial_users || 0);
    if (total === 0) return null;

    const premiumPercent = ((metrics.premium_users || 0) / total) * 100;
    const starterPercent = ((metrics.starter_users || 0) / total) * 100;
    const trialPercent = ((metrics.trial_users || 0) / total) * 100;

    // Calculate cumulative percentages for pie chart
    const starterEnd = starterPercent;
    const trialEnd = starterEnd + trialPercent;

    return (
      <div className="flex items-center justify-center gap-8">
        <div className="relative w-48 h-48">
          <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
            {/* Starter (Blue) */}
            <circle
              cx="18" cy="18" r="15.915"
              fill="transparent"
              stroke="#3b82f6"
              strokeWidth="3"
              strokeDasharray={`${starterPercent} ${100 - starterPercent}`}
              strokeDashoffset="0"
            />
            {/* Trial (Orange) */}
            <circle
              cx="18" cy="18" r="15.915"
              fill="transparent"
              stroke="#f97316"
              strokeWidth="3"
              strokeDasharray={`${trialPercent} ${100 - trialPercent}`}
              strokeDashoffset={`${-starterPercent}`}
            />
            {/* Premium (Green) */}
            <circle
              cx="18" cy="18" r="15.915"
              fill="transparent"
              stroke="#22c55e"
              strokeWidth="3"
              strokeDasharray={`${premiumPercent} ${100 - premiumPercent}`}
              strokeDashoffset={`${-trialEnd}`}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-bold text-slate-700">{total}</span>
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500"></div>
            <span className="text-sm font-medium">Premium: {metrics.premium_users || 0} ({premiumPercent.toFixed(1)}%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-500"></div>
            <span className="text-sm font-medium">Starter: {metrics.starter_users || 0} ({starterPercent.toFixed(1)}%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-orange-500"></div>
            <span className="text-sm font-medium">Trial: {metrics.trial_users || 0} ({trialPercent.toFixed(1)}%)</span>
          </div>
        </div>
      </div>
    );
  };

  // Render onboarding choice pie chart
  const renderOnboardingChoiceChart = () => {
    if (!onboardingMetrics?.choices) return null;
    
    const choices = onboardingMetrics.choices;
    const total = Object.values(choices).reduce((a, b) => a + b, 0);
    if (total === 0) return null;

    const choiceColors = {
      sale: { color: '#22c55e', label: 'Sale', icon: ShoppingCart },
      stock: { color: '#3b82f6', label: 'Stock', icon: Package },
      credit: { color: '#f97316', label: 'Credit', icon: CreditCard },
      invoice: { color: '#a855f7', label: 'Invoice', icon: Receipt }
    };

    let offset = 0;
    const segments = Object.entries(choices).map(([key, count]) => {
      const percent = (count / total) * 100;
      const segment = {
        key,
        percent,
        offset,
        color: choiceColors[key]?.color || '#64748b'
      };
      offset += percent;
      return segment;
    });

    return (
      <div className="flex items-center justify-center gap-8">
        <div className="relative w-40 h-40">
          <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
            {segments.map((seg) => (
              <circle
                key={seg.key}
                cx="18" cy="18" r="15.915"
                fill="transparent"
                stroke={seg.color}
                strokeWidth="3"
                strokeDasharray={`${seg.percent} ${100 - seg.percent}`}
                strokeDashoffset={`${-seg.offset + seg.percent}`}
              />
            ))}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xl font-bold text-slate-700">{total}</span>
          </div>
        </div>
        
        <div className="space-y-2">
          {Object.entries(choices).map(([key, count]) => {
            const info = choiceColors[key];
            const Icon = info?.icon;
            const percent = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
            return (
              <div key={key} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: info?.color || '#64748b' }}></div>
                {Icon && <Icon className="h-4 w-4 text-slate-500" />}
                <span className="text-sm font-medium capitalize">{info?.label || key}: {count} ({percent}%)</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Format time in MM:SS format as required
  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return '0:00';
    const totalMinutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${totalMinutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Format time for display in metric box (more readable)
  const formatTimeDisplay = (seconds) => {
    if (!seconds || seconds <= 0) return '0:00';
    const totalMinutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    if (totalMinutes >= 60) {
      const hours = Math.floor(totalMinutes / 60);
      const mins = totalMinutes % 60;
      return `${hours}h ${mins}m`;
    }
    return `${totalMinutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const totalPages = Math.ceil(totalUsers / perPage);

  if (loading && !metrics) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#021945] mx-auto mb-4" />
          <p className="text-slate-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-[#021945] text-white p-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/dashboard')}
            className="text-white hover:bg-white/10"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <BarChart3 className="h-6 w-6" />
            <h1 className="text-xl font-bold font-manrope">
              {language === 'hi' ? 'एनालिटिक्स' : 'Analytics'}
            </h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExport}
              disabled={exporting}
              className="text-white hover:bg-white/10"
              data-testid="export-btn"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {language === 'hi' ? 'एक्सपोर्ट' : 'Export to Excel'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchAnalytics}
              className="text-white hover:bg-white/10"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {language === 'hi' ? 'रिफ्रेश' : 'Refresh'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-8 space-y-6">
        {/* Date Filter */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex gap-2">
            {['today', '7days', '30days'].map((filter) => (
              <Button
                key={filter}
                variant={dateFilter === filter ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setDateFilter(filter);
                  setPage(1);
                }}
                className={dateFilter === filter ? 'bg-[#021945]' : ''}
                data-testid={`filter-${filter}`}
              >
                {filter === 'today' ? (language === 'hi' ? 'आज' : 'Today') :
                 filter === '7days' ? (language === 'hi' ? '7 दिन' : 'Last 7 days') :
                 (language === 'hi' ? '30 दिन' : 'Last 30 days')}
              </Button>
            ))}
          </div>
          
          {/* Custom Date Range */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={dateFilter === 'custom' ? 'default' : 'outline'}
                size="sm"
                className={dateFilter === 'custom' ? 'bg-[#021945]' : ''}
                data-testid="filter-custom"
              >
                <CalendarIcon className="h-4 w-4 mr-2" />
                {dateFilter === 'custom' && customDateRange.from ? (
                  <>
                    {format(customDateRange.from, 'dd/MM')}
                    {customDateRange.to && ` - ${format(customDateRange.to, 'dd/MM')}`}
                  </>
                ) : (
                  language === 'hi' ? 'कस्टम रेंज' : 'Custom Range'
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={customDateRange.from || new Date()}
                selected={{ from: customDateRange.from, to: customDateRange.to }}
                onSelect={(range) => {
                  setCustomDateRange({ from: range?.from, to: range?.to });
                  if (range?.from) {
                    setDateFilter('custom');
                    setPage(1);
                  }
                }}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Top Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white" data-testid="metric-active-users">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Activity className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-manrope">{metrics?.active_users_today || 0}</p>
                  <p className="text-sm text-slate-500">
                    {language === 'hi' ? 'आज एक्टिव' : 'Active Today'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white" data-testid="metric-premium-users">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Crown className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-manrope">{metrics?.premium_users || 0}</p>
                  <p className="text-sm text-slate-500">
                    {language === 'hi' ? 'प्रीमियम' : 'Premium Users'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white" data-testid="metric-trial-users">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Clock className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-manrope">{metrics?.trial_users || 0}</p>
                  <p className="text-sm text-slate-500">
                    {language === 'hi' ? 'ट्रायल' : 'Trial Users'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white" data-testid="metric-avg-session">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Timer className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-manrope">{formatTimeDisplay(metrics?.avg_session_time || 0)}</p>
                  <p className="text-sm text-slate-500">
                    {language === 'hi' ? 'औसत सत्र' : 'Avg Session'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Onboarding Metrics */}
        {onboardingMetrics && (
          <Card className="bg-white" data-testid="onboarding-metrics">
            <CardHeader>
              <CardTitle className="font-manrope flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-[#021945]" />
                {language === 'hi' ? 'ऑनबोर्डिंग एनालिटिक्स' : 'Onboarding Analytics'}
              </CardTitle>
              <CardDescription className="">
                {language === 'hi' ? 'नए उपयोगकर्ताओं का एक्टिवेशन' : 'New user activation metrics'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold text-[#021945]">{onboardingMetrics.completion_rate || 0}%</p>
                  <p className="text-sm text-slate-500">{language === 'hi' ? 'पूर्णता दर' : 'Completion Rate'}</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-3xl font-bold text-green-600">{onboardingMetrics.completed || 0}</p>
                  <p className="text-sm text-slate-500">{language === 'hi' ? 'पूर्ण' : 'Completed'}</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <p className="text-3xl font-bold text-orange-600">{onboardingMetrics.skipped || 0}</p>
                  <p className="text-sm text-slate-500">{language === 'hi' ? 'छोड़े गए' : 'Skipped'}</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-3xl font-bold text-blue-600">{formatTimeDisplay(onboardingMetrics.avg_time_to_complete || 0)}</p>
                  <p className="text-sm text-slate-500">{language === 'hi' ? 'औसत समय' : 'Avg Time'}</p>
                </div>
              </div>
              
              {/* Onboarding Choice Distribution */}
              {onboardingMetrics.choices && Object.keys(onboardingMetrics.choices).length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-4">
                    {language === 'hi' ? 'पहली एक्शन चॉइस' : 'First Action Choice Distribution'}
                  </h4>
                  {renderOnboardingChoiceChart()}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* User Distribution Chart */}
        <Card className="bg-white" data-testid="user-distribution-chart">
          <CardHeader>
            <CardTitle className="font-manrope flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-[#021945]" />
              {language === 'hi' ? 'उपयोगकर्ता वितरण' : 'User Distribution'}
            </CardTitle>
            <CardDescription className="">
              {language === 'hi' ? 'प्लान के अनुसार उपयोगकर्ता' : 'Users by plan type'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {renderPieChart()}
          </CardContent>
        </Card>

        {/* User Activity Table */}
        <Card className="bg-white" data-testid="user-activity-table">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <CardTitle className="font-manrope">
                {language === 'hi' ? 'उपयोगकर्ता गतिविधि' : 'User Activity'}
              </CardTitle>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={language === 'hi' ? 'खोजें...' : 'Search users...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-full md:w-64 h-10"
                  data-testid="user-search-input"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="">
                      {language === 'hi' ? 'क्लाइंट ID' : 'Client ID'}
                    </TableHead>
                    <TableHead className="">
                      {language === 'hi' ? 'नाम' : 'Name'}
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('sales_count')}
                    >
                      {language === 'hi' ? 'सेल्स' : 'Sales'} <SortIcon field="sales_count" />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('stock_count')}
                    >
                      {language === 'hi' ? 'स्टॉक' : 'Stock'} <SortIcon field="stock_count" />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('credit_count')}
                    >
                      {language === 'hi' ? 'क्रेडिट' : 'Credit'} <SortIcon field="credit_count" />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('invoice_count')}
                    >
                      {language === 'hi' ? 'इनवॉइस' : 'Invoices'} <SortIcon field="invoice_count" />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('logins')}
                    >
                      {language === 'hi' ? 'लॉगिन' : 'Logins'} <SortIcon field="logins" />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('time_spent')}
                    >
                      {language === 'hi' ? 'समय' : 'Time Spent'} <SortIcon field="time_spent" />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userActivity.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        {language === 'hi' ? 'कोई डेटा नहीं' : 'No data found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    userActivity.map((user) => (
                      <TableRow key={user.user_id}>
                        <TableCell className="font-mono text-sm">{user.unique_client_id}</TableCell>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell>{user.sales_count || 0}</TableCell>
                        <TableCell>{user.stock_count || 0}</TableCell>
                        <TableCell>{user.credit_count || 0}</TableCell>
                        <TableCell>{user.invoice_count || 0}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{user.logins || 0}</Badge>
                        </TableCell>
                        <TableCell>{formatTime(user.time_spent || 0)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  {language === 'hi' 
                    ? `${totalUsers} में से ${(page - 1) * perPage + 1}-${Math.min(page * perPage, totalUsers)} दिखा रहे हैं`
                    : `Showing ${(page - 1) * perPage + 1}-${Math.min(page * perPage, totalUsers)} of ${totalUsers}`}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm">{page} / {totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminAnalytics;
